import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const FeatureSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0); // 1 for right, -1 for left
  const [isOrderMenuOpen, setIsOrderMenuOpen] = useState(false);

  const slides = [
    {
      id: 1,
      day: 'Monday',
      title: 'House Margaritas',
      description: 'Enjoy our refreshing margaritas on the rocks for just $4.99',
      image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b',
      imageAlt: 'Refreshing house margarita on the rocks with salt rim and lime garnish'
    },
    {
      id: 2,
      day: 'Tuesday',
      title: 'Taco Tuesday',
      description: 'Get 3 tacos with your choice of meat plus rice or beans for only $12.99',
      image: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47',
      imageAlt: 'Three authentic Mexican tacos with assorted meats and fresh toppings'
    },
    {
      id: 3,
      day: 'Wednesday',
      title: 'Wine Night',
      description: 'Sip any glass of wine for just $4.99',
      image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3',
      imageAlt: 'Glass of red wine on elegant table setting'
    },
    {
      id: 4,
      day: 'Thursday',
      title: 'Draft Beer Special',
      description: 'Ice-cold 22oz draft beer for only $3.99',
      image: 'https://images.unsplash.com/photo-1608270586620-248524c67de9',
      imageAlt: 'Large draft beer in frosty glass with condensation'
    }
  ];


  const handleOrderLocation = (location) => {
    if (location === 'durham') {
      window.open('https://order.toasttab.com/online/kahlovera-street-tacos-durham', '_blank');
    } else if (location === 'chapel-hill') {
      window.open('https://order.toasttab.com/online/kahlovera-street-tacos-chapel-hill', '_blank');
    }
    setIsOrderMenuOpen(false);
  };

  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % slides?.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + slides?.length) % slides?.length);
  };

  const goToSlide = (index) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };

  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;
    if (info?.offset?.x > swipeThreshold) {
      prevSlide();
    } else if (info?.offset?.x < -swipeThreshold) {
      nextSlide();
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
    }),
    center: {
      x: 0,
    },
    exit: (direction) => ({
      x: direction > 0 ? -1000 : 1000,
    }),
  };

  return (
    <section className="py-16 bg-muted/30 pt-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12">

          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Sparkles" size={20} className="text-accent" />
            <span className="text-accent font-body font-medium">DAILY SPECIALS</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            This Week's <span className="text-primary">Specials</span>
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Something special every day of the week
          </p>
        </motion.div>

        {/* Slider */}
        <div className="relative">
          <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-warm-xl">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentSlide}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "tween", duration: 0.5, ease: "easeInOut" },
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                className="absolute inset-0 cursor-grab active:cursor-grabbing">

                {/* Background Image */}
                <Image
                  src={slides?.[currentSlide]?.image}
                  alt={slides?.[currentSlide]?.imageAlt}
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>

                {/* Content */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="p-8 sm:p-12 max-w-2xl text-center">
                    <div className="inline-block bg-accent text-accent-foreground px-4 py-2 rounded-full mb-4 font-bold">
                      <span className="font-body font-semibold text-sm uppercase">
                        {slides?.[currentSlide]?.day}
                      </span>
                    </div>
                    <h3 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
                      {slides?.[currentSlide]?.title}
                    </h3>
                    <p className="text-lg text-white/90 font-body mb-6">
                      {slides?.[currentSlide]?.description}
                    </p>
                    <div className="relative inline-block">
                      <Button
                        className="bg-accent text-accent-foreground hover:bg-accent/90 px-8 py-3 text-lg font-semibold rounded-lg shadow-warm hover:shadow-warm-lg transition-all duration-300 cursor-default">

                        Dine-in Only
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/20 transition-all duration-300 shadow-lg">

            <Icon name="ChevronLeft" size={24} className="text-black" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/20 transition-all duration-300 shadow-lg">

            <Icon name="ChevronRight" size={24} className="text-black" />
          </button>

          {/* Dots Navigation */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {slides?.map((_, index) =>
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/70'}`
              } />

            )}
          </div>
        </div>
      </div>
    </section>
  );

};

export default FeatureSlider;